import { useRef, useEffect } from 'react';

/**
 * Reveal — fades + lifts a block in as it enters the viewport,
 * fades it back out as it leaves. Driven by a single shared
 * rAF loop so any number of <Reveal>s on the page is cheap.
 *
 * Honors `prefers-reduced-motion` by skipping the animation.
 */

const targets = new Set();
let loopStarted = false;

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function startLoop() {
  if (loopStarted) return;
  loopStarted = true;
  let ticking = false;

  const tick = () => {
    ticking = false;
    const vh = window.innerHeight;
    for (const t of targets) {
      const el = t.el;
      if (!el) continue;
      const r = el.getBoundingClientRect();

      // Fade in as the top crosses 92% → 65% of viewport height.
      const enterFrom = vh * 0.92;
      const enterTo = vh * 0.65;
      const enterT = clamp01((enterFrom - r.top) / (enterFrom - enterTo));

      // Fade out as the bottom rises above 30% → -5% of viewport.
      const leaveFrom = vh * 0.3;
      const leaveTo = vh * -0.05;
      const leaveT = 1 - clamp01((leaveFrom - r.bottom) / (leaveFrom - leaveTo));

      const visibility = Math.min(enterT, leaveT);
      const eased = easeOutCubic(visibility);
      const lift = (1 - eased) * 24;

      el.style.opacity = String(eased);
      el.style.transform = `translate3d(0, ${lift}px, 0)`;
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tick);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  requestAnimationFrame(tick);
}

export default function Reveal({
  children,
  as: Tag = 'div',
  delay = 0,
  style,
  className,
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      if (ref.current) {
        ref.current.style.opacity = '1';
        ref.current.style.transform = 'none';
      }
      return undefined;
    }
    const target = { el: ref.current };
    targets.add(target);
    startLoop();
    return () => {
      targets.delete(target);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: 0,
        transform: 'translate3d(0, 24px, 0)',
        transition: `opacity 700ms var(--ease-out) ${delay}ms, transform 700ms var(--ease-out) ${delay}ms`,
        willChange: 'opacity, transform',
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
